// PlaylistSidebar.js
import React, { useContext, useState } from "react";
import PlaylistTile from "./PlaylistTile";

import { motion } from "framer-motion";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";

import { CurrentPlaylistContext, FAVOURITES_PLAYLIST_ID } from "../contexts/CurrentPlaylistContext";
import { FavouritesContext } from "../contexts/FavouritesContext";

const Playlist = () => {
  // Access the current playlist context
  const {
    currentPlaylist,
    // eslint-disable-next-line no-unused-vars
    currentVideoIndex,
    videos,
    activeVideoIndex,
    activePlaylist,
    startPlaying,
    setVideosDirectly,
  } = useContext(CurrentPlaylistContext);

  const { reorderFavourites } = useContext(FavouritesContext);

  const isFavourites = currentPlaylist === FAVOURITES_PLAYLIST_ID;
  const [isEditing, setIsEditing] = useState(false);

  // Highlight the active song only if this playlist is the one currently playing
  const isActivePlaylist = activePlaylist === currentPlaylist;

  // While browsing, the Player already shows the playlist name in its header
  const isBrowsingOnly = activePlaylist === null || activePlaylist !== currentPlaylist;

  const handleSongClick = (index) => {
    startPlaying(currentPlaylist, index, videos);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = videos.findIndex((v) => v.video_id === active.id);
    const newIndex = videos.findIndex((v) => v.video_id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    reorderFavourites(oldIndex, newIndex);

    // Sync the reordered list into the current videos view
    const updated = [...videos];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);
    setVideosDirectly(updated);
  };

  const playlistItems = videos.map((video, index) => (
    <PlaylistTile
      key={video.video_id}
      video={video}
      index={index}
      currentPlaylist={currentPlaylist}
      currentVideoIndex={isActivePlaylist ? activeVideoIndex : -1}
      showArtist={isFavourites}
      isEditing={isFavourites && isEditing}
      onClick={() => handleSongClick(index)}
    />
  ));

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
        className="playlist"
      >
        <div className="playlist-header">
          {isFavourites && !isBrowsingOnly ? "Oblíbené skladby" : "Seznam skladeb"}
          {isFavourites && (
            <i
              className={`fa-solid fa-pen playlist-edit-btn${isEditing ? " active" : ""}`}
              onClick={() => setIsEditing(!isEditing)}
              title={isEditing ? "Hotovo" : "Upravit"}
            ></i>
          )}
        </div>
        <hr></hr>
        <motion.div className="playlist-group">
          {isFavourites ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={videos.map((v) => v.video_id)}
                strategy={verticalListSortingStrategy}
              >
                {playlistItems}
              </SortableContext>
            </DndContext>
          ) : (
            playlistItems
          )}
        </motion.div>
      </motion.div>
    </>
  );
};

export default Playlist;
